import React, { useCallback, useMemo, useEffect } from 'react'
import { Alert } from 'reactstrap'
import FormikInputField from 'components/FormikInputField'
import SchoolLookUp from 'components/SchoolLookUp'
import SiteChangeConfirmation from 'components/EditLicenseModalSiteChangeConfirmation/component'
import ErrorBoundary from 'components/ErrorBoundary'
import { hasSiteChanged } from 'lib/netsuiteUtils'
import { SchoolLookUpSiteData } from 'types'

interface TabProps {
  formikProps: any
  selectedNetSuiteAsset: any
  selectedSites: any[]
  selectedSitesUsers: any[]
  onSchoolSelected: (site: any) => (formik: any) => void
  onCustomerIdValidate: (value: string) => (formik: any) => Promise<string>
  initialSiteData?: any
  isInitialLoad?: boolean
}

const AssigneeTab: React.FC<TabProps> = ({
  formikProps,
  onCustomerIdValidate,
  onSchoolSelected,
  initialSiteData,
  isInitialLoad = false,
}) => {
  const validateCustomerId = (value: string) => {
    return onCustomerIdValidate(value)(formikProps)
  }

  const handleSchoolSelection = useCallback(
    (siteData: SchoolLookUpSiteData) => {
      return onSchoolSelected(siteData)(formikProps)
    },
    [onSchoolSelected, formikProps],
  )

  // Get merged initial site data (prioritize initialSiteData over formikProps.initialValues)
  const mergedInitialSiteData = useMemo(() => {
    return {
      ...(formikProps.initialValues || {}),
      ...(initialSiteData || {}),
    } as Partial<SchoolLookUpSiteData>
  }, [formikProps.initialValues, initialSiteData])

  // Use shared utility to check if site has changed
  const checkSiteChanged = useCallback(
    (currentValues: Partial<SchoolLookUpSiteData>): boolean => {
      return hasSiteChanged(currentValues, mergedInitialSiteData, isInitialLoad)
    },
    [mergedInitialSiteData, isInitialLoad],
  )

  // Check for changes when nsCustomerId changes
  const handleCustomerIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formikProps.handleChange(e)
      // Check for site change after the value is updated
      const newValue = e.target.value

      // Create updated values object for comprehensive change detection
      const updatedValues = {
        ...formikProps.values,
        nsCustomerId: newValue,
      }

      const hasChanged = checkSiteChanged(updatedValues)
      const currentSiteWasChanged = formikProps.values.siteWasChanged

      // Only update if the state actually changed to prevent flickering
      if (hasChanged !== currentSiteWasChanged) {
        formikProps.setFieldValue('siteWasChanged', hasChanged)
        if (hasChanged) {
          formikProps.setFieldValue('siteChangeChecked', false)
        }
      }
    },
    [formikProps, checkSiteChanged],
  )

  // Check for site changes whenever form values change
  useEffect(() => {
    const hasChanged = checkSiteChanged(formikProps.values)
    const currentSiteWasChanged = formikProps.values.siteWasChanged

    // Only update if the state actually changed to prevent flickering
    if (hasChanged !== currentSiteWasChanged) {
      formikProps.setFieldValue('siteWasChanged', hasChanged)
      if (hasChanged) {
        formikProps.setFieldValue('siteChangeChecked', false)
      }
    }
  }, [formikProps.values, checkSiteChanged, formikProps.setFieldValue])

  // Create synchronized school data that reflects formik values
  const synchronizedSchoolData = useMemo<SchoolLookUpSiteData>(() => {
    const {
      nsCustomerId,
      netsuiteCustomerId,
      organizationName,
      companyName,
      city,
      country,
      region,
    } = formikProps.values
    return {
      nsCustomerId: nsCustomerId || '',
      netsuiteCustomerId: netsuiteCustomerId ?? null,
      organizationName: organizationName || '',
      companyName: companyName || '',
      city: city || '',
      country: country || '',
      region: region || '',
    }
  }, [
    formikProps.values.nsCustomerId,
    formikProps.values.netsuiteCustomerId,
    formikProps.values.organizationName,
    formikProps.values.companyName,
    formikProps.values.city,
    formikProps.values.country,
    formikProps.values.region,
  ])

  return (
    <div className="same-height">
      <ErrorBoundary>
        <Alert color="info">
          Assign license to site by entering the site's NetSuite Customer ID OR
          by selecting the site using dropdown menu.
          <br />
          If you use the dropdown menu, leave NetSuite Customer ID blank.
        </Alert>

        <FormikInputField
          label="Assigned Site's NetSuite Customer ID"
          name="nsCustomerId"
          type="text"
          validate={validateCustomerId}
          onChange={handleCustomerIdChange}
          {...formikProps}
        />

        <hr />

        <SchoolLookUp
          disabled={!!formikProps.values.nsCustomerId}
          fireOnLoad={!formikProps.values.nsCustomerId}
          onSelection={handleSchoolSelection}
          siteData={synchronizedSchoolData}
        />

        {/* Show site change confirmation if site has changed */}
        {formikProps.values.siteWasChanged && (
          <SiteChangeConfirmation
            setFieldValue={formikProps.setFieldValue}
            siteChangeChecked={formikProps.values.siteChangeChecked}
          />
        )}
      </ErrorBoundary>
    </div>
  )
}

export default AssigneeTab
